import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { AnyAction } from "redux";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { showMessage } from "../messages/messagesActions";
import { endPoints } from "../../constants/endPoints";
import { getApiErrorMessage } from "../common/helpers";
import { addSearchFilterToAdvanceFilter, getSearchQueryV2 } from "../common/helpers";
import {
    IPartnerComplaintLog,
    IPartnerComplaintByMechanicByCity,
    IPartnerComplaintReport,
    IComplaintSearchQueryParams,
    IVerifyComplaintPayload,
    IAssignMechanicPayload,
    IAddAdditionalRemarkPayload,
    IUpdateMechanicReachTimePayload,
    IUpdateComplaintAdditionalAmountPayload,
    ICloseOrCancelComplaintPayload
} from "./complaints.types";
import { getUserAuth } from "../../helpers";
import { ICloseComplaintFormValues } from "./complaints.state";

// ==================== COMPLAINT LOG ACTIONS ====================
export const FETCH_COMPLAINT_LOG_LIST_PROGRESS = "FETCH_COMPLAINT_LOG_LIST_PROGRESS";
export const FETCH_COMPLAINT_LOG_LIST_SUCCESS = "FETCH_COMPLAINT_LOG_LIST_SUCCESS";
export const FETCH_COMPLAINT_LOG_LIST_FAILED = "FETCH_COMPLAINT_LOG_LIST_FAILED";
export const CLEAR_COMPLAINT_LOG_STATE = "CLEAR_COMPLAINT_LOG_STATE";

export const fetchComplaintLogListProgress = () => action(FETCH_COMPLAINT_LOG_LIST_PROGRESS);
export const fetchComplaintLogListSuccess = (list: IPartnerComplaintLog[], totalRecords: number) =>
    action(FETCH_COMPLAINT_LOG_LIST_SUCCESS, { list, totalRecords });
export const fetchComplaintLogListFailed = () => action(FETCH_COMPLAINT_LOG_LIST_FAILED);
export const clearComplaintLogState = () => action(CLEAR_COMPLAINT_LOG_STATE);

export const fetchComplaintLogListAsync =
    (queryParams: IComplaintSearchQueryParams, partner_name = '', complaint_no = '', vehicle_number = '', membership_code = ''
        , paidStatus = '', mechanicType = '', mechanic_id_v = ''): ThunkAction<void, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(fetchComplaintLogListProgress());
                const {
                    page = 1,
                    rowsPerPage = 25,
                    status,
                    date,
                    advancedFilters,
                    partner_id: queryPartnerId,
                } = queryParams;

                const fromDateStr = date?.fromDate || "";
                const toDateStr = date?.toDate || "";

                let apiURL = `${endPoints.partners.getRsaComplaintLog}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

                if (fromDateStr && toDateStr) {
                    apiURL += `&from_date=${fromDateStr}&to_date=${toDateStr}`;
                }

                if (
                    status === "OPEN" ||
                    status === "CANCELLED" ||
                    status === "CLOSED" ||
                    status === "NEW" ||
                    status === "DROPPED"
                ) {
                    apiURL += `&status=${status}`;
                }

                if (queryPartnerId !== undefined && queryPartnerId !== null && queryPartnerId !== 'string') {
                    apiURL += `&partner_id=${queryPartnerId}`;
                }

                if (paidStatus === "PAID" || paidStatus === "UNPAID") {
                    apiURL += `&paid_status_v=${paidStatus}`;
                }

                if (mechanicType && mechanicType !== "-1" && mechanicType !== 'string') {
                    apiURL += `&mechanic_type_v=${mechanicType}`;
                }

                if (mechanic_id_v && mechanic_id_v !== 'string') {
                    apiURL += `&mechanic_id_v=${mechanic_id_v}`;
                }

                // Support advanced search filters as well
                const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

                if (partner_name && partner_name !== 'string') {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["partner_name"], partner_name);
                }
                if (complaint_no && complaint_no !== 'string') {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["complaint_no"], complaint_no);
                }
                if (vehicle_number && vehicle_number !== 'string') {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["vehicle_number"], vehicle_number);
                }
                if (membership_code && membership_code !== 'string') {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["membership_code"], membership_code);
                }

                if (advanceFilter.length > 0) {
                    apiURL += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
                }

                const res = await api.get(apiURL);

                const data: IPartnerComplaintLog[] = res.data.data || res.data.result || [];
                const totalRecords = res.data.totalRecords || 0;

                dispatch(fetchComplaintLogListSuccess(data, totalRecords));
            } catch (err: any) {
                dispatch(fetchComplaintLogListFailed());
                dispatch(
                    showMessage({
                        message: err?.response?.data?.message || "Failed to fetch complaint logs",
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
            }
        };

// ==================== COMPLAINT BY MECHANIC BY CITY ACTIONS ====================
export const FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_PROGRESS = "FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_PROGRESS";
export const FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_SUCCESS = "FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_SUCCESS";
export const FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_FAILED = "FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_FAILED";
export const CLEAR_COMPLAINT_BY_MECHANIC_BY_CITY_STATE = "CLEAR_COMPLAINT_BY_MECHANIC_BY_CITY_STATE";

export const fetchComplaintByMechanicByCityListProgress = () => action(FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_PROGRESS);
export const fetchComplaintByMechanicByCityListSuccess = (list: IPartnerComplaintByMechanicByCity[], totalRecords: number) =>
    action(FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_SUCCESS, { list, totalRecords });
export const fetchComplaintByMechanicByCityListFailed = () => action(FETCH_COMPLAINT_BY_MECHANIC_BY_CITY_LIST_FAILED);
export const clearComplaintByMechanicByCity = () => action(CLEAR_COMPLAINT_BY_MECHANIC_BY_CITY_STATE);

export const fetchComplaintByMechanicByCity =
    (queryParams: IComplaintSearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(fetchComplaintByMechanicByCityListProgress());

                const {
                    page,
                    rowsPerPage,
                    status,
                    date,
                    advancedFilters,
                    companyOutletUuid,
                    assigned_mechanic_id,
                    mechanic_id,
                    service_city,
                    total_complaints,
                    open_complaints,
                    closed_complaints,
                    partner_name,
                    partner_id,
                    type
                } = queryParams;

                const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

                const query = getSearchQueryV2({
                    ...queryParams,
                    status: status && status !== "-1" ? status : "-1",
                    advancedFilters: advanceFilter,
                    customFilters: {
                        type,
                        ...(partner_id && { partner_id }),
                        ...(partner_name && { partner_name }),
                        ...(mechanic_id && { mechanic_id }),
                        ...(service_city && { city: service_city }),
                    }
                });

                const res = await api.get(`${endPoints.partners.getComplaintReport}?${query}`);
                const data: IPartnerComplaintByMechanicByCity[] = res.data.data || [];
                const totalRecords = res.data.totalRecords || 0;

                dispatch(fetchComplaintByMechanicByCityListSuccess(data, totalRecords));
            } catch (err: any) {
                dispatch(fetchComplaintByMechanicByCityListFailed());
                dispatch(
                    showMessage({
                        message: err?.response?.data?.message || "Failed to fetch complaint logs",
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
            }
        };

// ==================== COMPLAINT REPORT ACTIONS ====================
export const FETCH_COMPLAINT_REPORT_LIST_PROGRESS = "FETCH_COMPLAINT_REPORT_LIST_PROGRESS";
export const FETCH_COMPLAINT_REPORT_LIST_SUCCESS = "FETCH_COMPLAINT_REPORT_LIST_SUCCESS";
export const FETCH_COMPLAINT_REPORT_LIST_FAILED = "FETCH_COMPLAINT_REPORT_LIST_FAILED";
export const CLEAR_COMPLAINT_REPORT_DETAILS_LIST_STATE = "CLEAR_COMPLAINT_REPORT_DETAILS_LIST_STATE";

export const fetchComplaintReportListProgress = () => action(FETCH_COMPLAINT_REPORT_LIST_PROGRESS);
export const fetchComplaintReportListSuccess = (list: IPartnerComplaintReport[], totalRecords: number) =>
    action(FETCH_COMPLAINT_REPORT_LIST_SUCCESS, { list, totalRecords });
export const fetchComplaintReportListFailed = () => action(FETCH_COMPLAINT_REPORT_LIST_FAILED);
export const clearComplaintReportDetailsListState = () => action(CLEAR_COMPLAINT_REPORT_DETAILS_LIST_STATE);

export const fetchComplaintReportListAsync =
    (queryParams: IComplaintSearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(fetchComplaintReportListProgress());

                const {
                    page,
                    rowsPerPage,
                    status,
                    date,
                    advancedFilters,
                    companyOutletUuid,
                    complaint_no,
                    vehicle_number,
                    membership_code,
                    mechanic_id,
                    mechanic_name,
                    service_city,
                    partner_name,
                    partner_id,
                    reg_contact_no,
                } = queryParams;

                const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

                addSearchFilterToAdvanceFilter(advanceFilter, ["complaint_no"], complaint_no);
                addSearchFilterToAdvanceFilter(advanceFilter, ["vehicle_number"], vehicle_number);
                addSearchFilterToAdvanceFilter(advanceFilter, ["membership_code"], membership_code);
                addSearchFilterToAdvanceFilter(advanceFilter, ["reg_contact_no"], reg_contact_no);

                if (service_city) {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["service_city"], service_city);
                }
                if (partner_name) {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["partner_name"], partner_name);
                }

                if (mechanic_name) {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["mechanic_name"], mechanic_name);
                }

                if (mechanic_id) {
                    addSearchFilterToAdvanceFilter(advanceFilter, ["assigned_mechanic_id"], mechanic_id);
                }

                const query = getSearchQueryV2({
                    ...queryParams,
                    status: status && status !== "-1" ? status : "-1",
                    advancedFilters: advanceFilter,
                    customFilters: {
                        ...(partner_id && { partner_id }),
                    }
                });

                const res = await api.get(`${endPoints.crossroadsLeads.getCrossroadsComplaintLog}?${query}`);
                const data: IPartnerComplaintLog[] = res.data.data || [];
                const totalRecords = res.data.totalRecords || 0;

                dispatch(fetchComplaintReportListSuccess(data, totalRecords));
            } catch (err: any) {
                dispatch(fetchComplaintReportListFailed());
                dispatch(
                    showMessage({
                        message: err?.response?.data?.message || "Failed to fetch crossroads complaint logs",
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
            }
        };

// ==================== CHECK VEHICLE PHONE EXIST ACTIONS ====================
export const CHECK_VEHICLE_PHONE_EXIST_PROGRESS = "CHECK_VEHICLE_PHONE_EXIST_PROGRESS";
export const CHECK_VEHICLE_PHONE_EXIST_SUCCESS = "CHECK_VEHICLE_PHONE_EXIST_SUCCESS";
export const CHECK_VEHICLE_PHONE_EXIST_FAILED = "CHECK_VEHICLE_PHONE_EXIST_FAILED";

export const checkVehiclePhoneExistProgress = () => action(CHECK_VEHICLE_PHONE_EXIST_PROGRESS);
export const checkVehiclePhoneExistSuccess = (data: any) => action(CHECK_VEHICLE_PHONE_EXIST_SUCCESS, { data });
export const checkVehiclePhoneExistFailed = (errorMessage: string) => action(CHECK_VEHICLE_PHONE_EXIST_FAILED, { errorMessage });

export const checkVehiclePhoneExistAsync = (
    searchType: string,
    value: string,
    onCallback?: (isSuccess: boolean, data?: any) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            dispatch(checkVehiclePhoneExistProgress());

            const baseUrl =
                searchType === "Mobile Number"
                    ? (process.env.REACT_APP_SERVER2_URL || process.env.REACT_APP_COMPLAINT_SEARCH_URL || process.env.REACT_APP_SERVER_URL)
                    : (process.env.REACT_APP_COMPLAINT_SEARCH_URL || process.env.REACT_APP_SERVER_URL);

            const url =
                searchType === "Mobile Number"
                    ? `${baseUrl}/check-vehicle-phone-exist?phone_number=${value}`
                    : searchType === "Vehicle Number"
                        ? `${baseUrl}/check-vehicle-phone-exist?vehicle_number=${value}`
                        : `${baseUrl}/check-vehicle-phone-exist?certificate_number=${value}`;

            const res = await api.get(url);

            const data = res.data?.data || [];
            dispatch(checkVehiclePhoneExistSuccess(data));
            onCallback?.(true, data);
        } catch (err: any) {
            const errorMsg = err?.response?.data?.message || "Failed to check vehicle/phone existence";
            dispatch(checkVehiclePhoneExistFailed(errorMsg));
            onCallback?.(false, null);
        }
    };

// ==================== ASSIGN MECHANIC ACTIONS ====================
export const assignMechanicAsync = (
    type: "assign" | "change",
    payload: IAssignMechanicPayload,
    onCallback?: (isSuccess: boolean, errorMsg?: string) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            const url =
                type === "assign"
                    ? "/bo-assign-mechanic"
                    : "/update-mechanic-after-complaint-closed";

            await api.post(url, payload);

            onCallback?.(true);
        } catch (err: any) {
            const errorMsg = err?.response?.data?.message || err?.response?.data?.msg || "Failed to assign/change mechanic";
            onCallback?.(false, errorMsg);
        }
    };

// ==================== ADD ADDITIONAL REMARK ACTIONS ====================
export const addAdditionalRemarkAsync = (
    payload: IAddAdditionalRemarkPayload,
    onCallback?: (isSuccess: boolean, errorMsg?: string) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            await api.post("/bo-add-additional-remarks", payload);

            onCallback?.(true);
        } catch (err: any) {
            onCallback?.(false, getApiErrorMessage(err, "Failed to add additional remarks"));
        }
    };

// ==================== UPDATE MECHANIC REACH TIME ACTIONS ====================
export const updateMechanicReachTimeAsync = (
    payload: IUpdateMechanicReachTimePayload,
    onCallback?: (isSuccess: boolean, errorMsg?: string) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            await api.post(
                "/update-mechanic-reach-time-after-mechanic-assigned",
                payload
            );

            onCallback?.(true);
        } catch (err: any) {
            onCallback?.(false, getApiErrorMessage(err, "Failed to update mechanic reach time"));
        }
    };

// ==================== UPDATE COMPLAINT ADDITIONAL AMOUNT ACTIONS ====================
export const updateComplaintAdditionalAmountAsync = (
    payload: IUpdateComplaintAdditionalAmountPayload,
    onCallback?: (isSuccess: boolean, errorMsg?: string) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            await api.post("/update-complaint-additional-amount", payload);

            onCallback?.(true);
        } catch (err: any) {
            onCallback?.(false, getApiErrorMessage(err, "Failed to update additional amount"));
        }
    };

// ==================== UPDATE MECHANIC AMOUNT ACTIONS ====================
export const updateMechanicAmountAsync = (
    payload: { complaint_no: number; amount: string | number },
    onCallback?: (isSuccess: boolean, errorMsg?: string) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            await api.post("/update-mechanic-amount", payload);

            onCallback?.(true);
        } catch (err: any) {
            onCallback?.(false, getApiErrorMessage(err, "Failed to update mechanic amount"));
        }
    };

// ==================== CLOSE OR CANCEL COMPLAINT ACTIONS ====================
export const closeOrCancelComplaintAsync = (
    values: ICloseComplaintFormValues,
    complaint: IPartnerComplaintLog,
    isOtherRemark: boolean,
    onCallback?: (isSuccess: boolean, errorMsg?: string) => void
): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            // Type guard function
            const isValueAmountObject = (obj: any): obj is { value: string; amount: string } => {
                return typeof obj === 'object' && obj !== null && 'value' in obj && 'amount' in obj;
            };

            // Format additional_details_v: handle new structure with value and amount
            const formattedAdditionalDetails = values.additional_details_v.map((item: any) => {
                const key = Object.keys(item)[0];
                const valueObj = Object.values(item)[0];

                // Handle both old format (string) and new format (object with value and amount)
                if (isValueAmountObject(valueObj)) {
                    // New format: { value: string, amount: string }
                    let formattedValue = valueObj.value;

                    // Check if service key is "Towing" (case-insensitive)
                    if (key?.toUpperCase() === "TOWING") {
                        // Remove any existing "KM" to avoid duplication, then append "KM"
                        const cleanValue = formattedValue?.toString().replace(/\s*KM\s*$/i, "").trim();
                        formattedValue = cleanValue ? `${cleanValue} KM` : formattedValue;
                    } else {
                        // For non-Towing services, remove any "KM" suffix
                        formattedValue = formattedValue?.toString().replace(/\s*KM\s*$/i, "").trim() || formattedValue;
                    }

                    return { [key]: { value: formattedValue, amount: valueObj.amount || "" } };
                } else {
                    // Old format: string (backward compatibility)
                    const value = valueObj as string;
                    if (key?.toUpperCase() === "TOWING") {
                        const cleanValue = value?.toString().replace(/\s*KM\s*$/i, "").trim();
                        return { [key]: cleanValue ? `${cleanValue} KM` : value };
                    } else {
                        const cleanValue = value?.toString().replace(/\s*KM\s*$/i, "").trim();
                        return { [key]: cleanValue || value };
                    }
                }
            });

            // Get the selected remark (either from dropdown or other_remark_v)
            const selectedRemark = isOtherRemark
                ? values.other_remark_v
                : values.remark_v;

            // Prepend the existing remark from complaint if it exists
            const finalRemark = complaint.remark && complaint.remark.trim()
                ? `${complaint.remark.trim()} / ${selectedRemark}`
                : selectedRemark;

            const payload = {
                ...values,
                additional_details_v: formattedAdditionalDetails,
                close_category_v: values.service_status_v,
                remark_v: finalRemark,
            };

            await api.post("/bo-complaint-close-or-cancel-by-controlRoom", payload);

            dispatch(
                showMessage({
                    message: "Complaint closed successfully!!",
                    type: "success",
                    displayAs: "snackbar",
                })
            );
            onCallback?.(true);
        } catch (err: any) {
            const errorMsg = getApiErrorMessage(err, "Failed to close/cancel complaint.");
            dispatch(
                showMessage({
                    message: errorMsg,
                    type: "error",
                    displayAs: "snackbar",
                })
            );
            onCallback?.(false, errorMsg);
        }
    };

// ==================== VERIFY COMPLAINT ACTIONS ====================
export const verifyComplaintAsync = (
    complaintData: IVerifyComplaintPayload
): ThunkAction<Promise<void>, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            await api.post(
                "/modify-complaint-by-cc",
                complaintData
            );
        } catch (err) {
            throw err;
        }
    };

// ==================== CLOSE COMPLAINT DIALOG HELPERS ====================
export const sendComplaintOtpAsync = (
    complaintNo: number,
    onCallback?: (isSuccess: boolean, errorMsg?: string) => void
): ThunkAction<Promise<void>, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            await api.post(
                "/send-complaint-otp-by-mechanic",
                { complaint_no: complaintNo }
            );
            onCallback?.(true);
        } catch (err: any) {
            onCallback?.(false, getApiErrorMessage(err, "Failed to send OTP"));
        }
    };

export const getMyComplaintOtpAsync = (
    onCallback?: (isSuccess: boolean, otp?: string, errorMsg?: string) => void
): ThunkAction<Promise<void>, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            const response = await api.get("/get-my-complaint-otp");
            const receivedOtp = response.data.otp;
            onCallback?.(true, receivedOtp);
        } catch (err: any) {
            onCallback?.(false, undefined, getApiErrorMessage(err, "Failed to get OTP"));
        }
    };

export const uploadComplaintDocAsync = (
    formdata: FormData,
    onCallback?: (isSuccess: boolean, fileUrl?: string, errorMsg?: string) => void
): ThunkAction<Promise<void>, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            const res = await api.post("/upload-docs", formdata);
            const fileUrl = res.data.fileUrl || res.data.Location || res.data.Key || "";
            onCallback?.(true, fileUrl);
        } catch (err: any) {
            onCallback?.(false, undefined, getApiErrorMessage(err, "Failed to upload document"));
        }
    };

export const fetchAllComplaintsDataAsync = (
    fromDate: string,
    toDate: string,
    selectedTab: string,
    searchState: any[]
): ThunkAction<Promise<any[]>, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
        try {
            const token = getUserAuth();
            let apiURL = `${endPoints.partners.getRsaComplaintLog}?from_date=${fromDate}&to_date=${toDate}`;

            if (
                selectedTab === "OPEN" ||
                selectedTab === "CANCELLED" ||
                selectedTab === "CLOSED" ||
                selectedTab === "NEW" ||
                selectedTab === "DROPPED"
            ) {
                apiURL += `&status=${selectedTab}`;
            }

            if (searchState && searchState.length > 0) {
                apiURL += `&advanceFilter=${JSON.stringify(searchState)}`;
            }

            const res = await api.get(apiURL);
            return res.data?.data || res.data?.result || [];
        } catch (err: any) {
            console.error("Error fetching complaint logs:", err);
            return [];
        }
    };
